import { useFrame } from '@react-three/fiber';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { type Group, Box3, Object3D, Vector3 } from 'three';

import useTilesStore from 'state/tilesStore';
import type { GroupProps } from 'types/r3f';

import { type Place, locationToEcef, locationToSurfaceRotation } from './geo';
import type { TileDefinition } from './loader';
import Tile from './Tile';

export interface TilesProps extends GroupProps {}

export interface TilesRef {
  container: Object3D;
  initialize: (place: Place, tileDefinitions: TileDefinition[]) => void;
  reset: () => void;
}

const Tiles = forwardRef<TilesRef, TilesProps>(({ ...props }, ref) => {
  const [hemisphereRadius, setHemisphereRadius] = useState(0);

  const setAttribution = useTilesStore(s => s.setAttribution);

  const containerRef = useRef<Group>(null!);
  const tileGroupRef = useRef<Group>(null!);
  const isInitializedRef = useRef(false);

  const initializeAttribution = (tiles: Tile[]) => {
    // Required by the Google 3D Tiles API TOS
    const copyrightMap = new Map<string, number>();

    for (const tile of tiles) {
      if (!tile.copyright) continue;
      for (const part of tile.copyright.split(';')) {
        const count = copyrightMap.get(part) ?? 0;
        copyrightMap.set(part, count + 1);
      }
    }

    const copyrightArray = Array.from(copyrightMap);
    copyrightArray.sort((a, b) => b[1] - a[1]);

    const attribution = copyrightArray.map(c => c[0]);
    setAttribution(attribution);
  };

  const initialize = (place: Place, tileDefinitions: TileDefinition[]) => {
    if (isInitializedRef.current) return;

    const targetQuat = locationToSurfaceRotation(place);
    tileGroupRef.current.setRotationFromQuaternion(targetQuat);

    // The ECEF (0, 0, 0) corresponds to the center of the Earth.
    // The following variable stores the vector
    // that goes from the center of the Earth (ECEF (0, 0, 0))
    // to the specific point on the surface (our place).
    const center = locationToEcef(place);
    // Adjust the ECEF axes, so they correspond to our Three.js axes.
    // Negate the result, so that adding this offset vector
    // to the tile positions places them around the center of our Three.js scene.
    const offset = new Vector3(center.x, center.z, -center.y).negate();

    // Offset is always equal for all tiles inside the scene,
    // so their relative positions stay the same.
    const tiles = tileDefinitions.map(td => {
      return new Tile({
        uid: td.uid,
        url: td.url,
        offset: offset,
        tint: td.tint,
      });
    });

    initializeAttribution(tiles);

    tileGroupRef.current.children.forEach(child => child.removeFromParent());
    tileGroupRef.current.add(...tiles);

    // useMatrixUpdateHandler switches matrixAutoUpdate off across this subtree
    // once the tiles are rendered. When that has already happened, the rotation
    // set above and the transforms of the tiles just added would never be
    // composed into their matrices, leaving them at whatever orientation the
    // previous matrices held. Compose them explicitly instead of relying on
    // three doing it for us.
    tileGroupRef.current.updateMatrix();
    tileGroupRef.current.traverse(node => node.updateMatrix());
    tileGroupRef.current.updateMatrixWorld(true);

    isInitializedRef.current = true;
  };

  useImperativeHandle(ref, () => ({
    container: containerRef.current,
    initialize,
    reset: () => {
      isInitializedRef.current = false;
    },
  }));

  useFrame(() => {
    if (isInitializedRef.current) {
      const aabb = new Box3();
      aabb.setFromObject(tileGroupRef.current);
      setHemisphereRadius(Math.abs(aabb?.min?.x / 1.5) ?? 0);
    }
  });

  return (
    <group ref={containerRef} {...props}>
      <group name="tilesContainer" ref={tileGroupRef} />

      <mesh position={[0, -100, 0]}>
        <sphereGeometry
          args={[
            hemisphereRadius ? 500 : 0,
            32,
            32,
            0,
            Math.PI * 2,
            Math.PI / 2,
            Math.PI / 2,
          ]}
        />
        <meshBasicMaterial color="black" side={1} />
      </mesh>
    </group>
  );
});

Tiles.displayName = 'Tiles';

export default Tiles;
